import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';

import { apiUrl } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { SocketService } from './socket.service';

/**
 * Notification Centre (web) — reads the shared persistent inbox backed by
 * vd_notification_queue via the same endpoints the mobile app uses:
 *   POST /api/notifications/list         { limit, offset, unread_only }
 *   GET  /api/notifications/unread_count
 *   POST /api/notifications/mark_read    { ids[] | all:true }
 *
 * Holds a live unread count (BehaviorSubject) for the header bell badge.
 * Web + mobile share the SAME vd_notification_queue, so a user sees the same
 * notifications (leave, feed, records, tasks) on both.
 */
export interface InboxItem {
  id: number;
  event_type: string;
  entity_type: string;
  entity_id: number | null;
  title: string | null;
  body: string | null;
  payload: any;
  is_read: boolean;
  created_at: string;
  read_at: string | null;
  module?: string;
}

export interface InboxModule { key: string; label: string; count: number; unread: number; }
export interface InboxPage { items: InboxItem[]; total: number; modules: InboxModule[]; }
export interface InboxLink { path: string; query?: any; }

@Injectable({ providedIn: 'root' })
export class NotificationFeedService {

  apiLink = apiUrl + '/api/';

  private _unread$ = new BehaviorSubject<number>(0);
  readonly unread$ = this._unread$.asObservable();
  get unread(): number { return this._unread$.value; }

  private refreshTimer: any = null;
  private lastRefresh = 0;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private socket: SocketService,
  ) {
    // Any realtime event may have created an inbox row → refresh the badge.
    // Leading-edge: the FIRST event refreshes immediately (feels instant);
    // bursts within 400ms collapse into a single trailing refresh.
    this.socket.events$.subscribe(() => this.onRealtimeEvent());
  }

  private onRealtimeEvent(): void {
    const now = Date.now();
    if (now - this.lastRefresh >= 400) {
      this.lastRefresh = now;
      this.refreshUnread();
    } else if (!this.refreshTimer) {
      this.refreshTimer = setTimeout(() => {
        this.refreshTimer = null;
        this.lastRefresh = Date.now();
        this.refreshUnread();
      }, 400);
    }
  }

  private async headers(): Promise<HttpHeaders> {
    const ud: any = await this.authService.getUserDetails();
    const token = ud && ud.token ? ud.token : '';
    return new HttpHeaders({ 'Authorization': `Bearer: ${token}` });
  }

  async refreshUnread(): Promise<void> {
    try {
      const headers = await this.headers();
      const res: any = await this.http.get(this.apiLink + 'notifications/unread_count', { headers }).toPromise();
      if (res && res.status === 1) this._unread$.next(Number(res.unread || 0));
    } catch { /* keep previous count */ }
  }

  async list(limit = 20, offset = 0, unreadOnly = false): Promise<InboxItem[]> {
    try {
      const headers = await this.headers();
      const res: any = await this.http.post(
        this.apiLink + 'notifications/list',
        { limit, offset, unread_only: unreadOnly },
        { headers }
      ).toPromise();
      return res && res.status === 1 && Array.isArray(res.notifications) ? res.notifications : [];
    } catch { return []; }
  }

  /** The Notifications page: filtered list + total + per-module counts. */
  async page(opts: { limit?: number; offset?: number; unreadOnly?: boolean; module?: string | null;
                     from?: string | null; to?: string | null }): Promise<InboxPage | null> {
    try {
      const headers = await this.headers();
      const res: any = await this.http.post(this.apiLink + 'notifications/list', {
        limit: opts.limit || 30, offset: opts.offset || 0, unread_only: !!opts.unreadOnly,
        module: opts.module || null, from: opts.from || null, to: opts.to || null, with_meta: true,
      }, { headers }).toPromise();
      if (!res || res.status !== 1) return null;
      return { items: res.notifications || [], total: Number(res.total || 0), modules: res.modules || [] };
    } catch { return null; }
  }

  /** Delete the given notifications (the caller's own only — enforced server-side). */
  async remove(ids: number[]): Promise<boolean> {
    if (!ids || !ids.length) return false;
    try {
      const headers = await this.headers();
      const res: any = await this.http.post(this.apiLink + 'notifications/delete', { ids }, { headers }).toPromise();
      await this.refreshUnread();
      return !!(res && res.status === 1);
    } catch { return false; }
  }

  // ── Where a notification leads (web) — shared by the bell and the Notifications page ──
  // Deep link: the exact record where the target screen can open one (?open=<id>),
  // otherwise the right list / tab. Mobile has its own routing (not done here).
  linkFor(n: InboxItem): InboxLink | null {
    const et = (n.entity_type || '').toLowerCase();
    const ev = (n.event_type || '').toLowerCase();
    const p = n.payload || {};
    const id = n.entity_id;
    if (et === 'data_record') {
      const tid = Number(p.template_id || 0);
      if (tid && id) return { path: '/user/data-entry/records/' + tid, query: { open: id } };
      return (ev.indexOf('submitted') >= 0 || ev.indexOf('approved_l1') >= 0)
        ? { path: '/user/data-entry/approval-queue' } : { path: '/user/data-entry/records' };
    }
    if (et === 'activity_execution') {
      const eid = Number(p.execution_id || id || 0);
      return { path: '/user/activity/execution', query: eid ? { open: eid } : undefined };
    }
    if (et === 'leave') return { path: '/user/employee/leave' };
    // R38 — feed notifications come from the new Feed (feedRoute.js); /user/post is the old newsfeed.
    if (et === 'feed') return { path: '/user/feed' };
    // Batch 12 (owner D7): open the task itself — a project task on that project's board, a
    // general task (or an older notification without a project) in My Tasks.
    if (et === 'task' || et === 'metask') {
      const pid = Number(p.project_id || 0);
      if (id && pid) return { path: '/user/metask/project/' + pid, query: { open: id } };
      if (id) return { path: '/user/metask/my-tasks', query: { open: id } };
      return { path: '/user/metask' };
    }
    if (et === 'ticket') return { path: id ? '/user/ticket-system/detail/' + id : '/user/ticket-system/manage' };
    // R39 — root-cause alerts: ?fb= auto-opens that analysis.
    if (et === 'fishbone') return { path: '/user/rootcause/fishbone', query: id ? { fb: id } : undefined };
    if (et === 'filemgr') return { path: '/user/file-manager' };
    // RE-6b — "statement ready" opens the statement itself.
    if (et === 'report_statement') return { path: id ? '/user/reports-hub/statements/' + id : '/user/reports-hub/statements' };
    // RE-7 — submitted / approved / returned narrative report.
    if (et === 'narrative_report') return { path: id ? '/user/reports-hub/narrative/' + id : '/user/reports-hub/narrative' };
    // Expense claims — the approver lands on "To approve", the claimant on "My claims".
    if (et === 'expense_claim') return { path: '/user/employee/expense-claims', query: { tab: p.tab === 'approve' ? 'approve' : 'mine' } };
    if (et === 'approval_flow') {
      const ft = String(p.flow_type || '').toLowerCase();
      if (ft === 'data_record') return { path: '/user/data-entry/approval-queue' };
      if (ft === 'leave') return { path: '/user/employee/leave' };
      if (ft === 'activity_execution') return { path: '/user/activity/execution' };
      if (ft === 'expense_claim') return { path: '/user/employee/expense-claims', query: { tab: 'approve' } };
    }
    // Last resort: a server-supplied in-app route.
    if (typeof p.route === 'string' && p.route.indexOf('/user/') === 0) return { path: p.route };
    return null;
  }

  iconFor(n: InboxItem): string {
    const et = (n.entity_type || '').toLowerCase();
    if (et === 'leave') return 'fa-calendar-check-o';
    if (et === 'data_record') return 'fa-file-text-o';
    if (et === 'report_statement') return 'fa-file-text';
    if (et === 'narrative_report') return 'fa-file-word-o';
    if (et === 'activity_execution') return 'fa-check-square-o';
    if (et === 'task' || et === 'metask') return 'fa-tasks';
    if (et === 'feed') return 'fa-bullhorn';
    if (et === 'ticket') return 'fa-life-ring';
    if (et === 'fishbone') return 'fa-sitemap';
    if (et === 'filemgr') return 'fa-folder-open-o';
    if (et === 'approval_flow') return 'fa-random';
    if (et === 'expense_claim') return 'fa-money';
    return 'fa-bell';
  }

  /** R40 — never show a raw code: the server's title, else a readable form of the event. */
  titleFor(n: InboxItem): string {
    if (n.title) return n.title;
    const ev = String(n.event_type || '').replace(/[._]/g, ' ').toLowerCase().trim();
    return ev ? ev.charAt(0).toUpperCase() + ev.slice(1) : 'Notification';
  }

  async markRead(ids: number[]): Promise<void> {
    if (!ids || !ids.length) return;
    try {
      const headers = await this.headers();
      await this.http.post(this.apiLink + 'notifications/mark_read', { ids }, { headers }).toPromise();
      this._unread$.next(Math.max(0, this.unread - ids.length));
    } catch { /* ignore */ }
  }

  async markAllRead(): Promise<void> {
    try {
      const headers = await this.headers();
      await this.http.post(this.apiLink + 'notifications/mark_read', { all: true }, { headers }).toPromise();
      this._unread$.next(0);
    } catch { /* ignore */ }
  }

  /**
   * Delete notifications. readOnly=true clears only already-read ones (unread
   * kept); readOnly=false clears everything. Refreshes the badge afterwards.
   */
  async clear(readOnly: boolean): Promise<boolean> {
    try {
      const headers = await this.headers();
      const res: any = await this.http.post(
        this.apiLink + 'notifications/clear', { read_only: readOnly }, { headers }
      ).toPromise();
      if (!readOnly) this._unread$.next(0);
      else await this.refreshUnread();
      return !!(res && res.status === 1);
    } catch { return false; }
  }
}
